import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Users, Ban, RotateCcw, Star, StickyNote } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, PLAN_LABEL } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SOURCE = { owner_grant: "Granted by you", stripe: "Paid via Stripe", owner: "Set by you" };

function VipStar({ c, onSaved }) {
  const toggle = async () => {
    try {
      const r = await api.put(`/owner/users/${c.id}/note`, { vip: !c.vip });
      onSaved({ ...c, ...r.data });
      toast.success(r.data.vip ? `${c.email} marked VIP` : "VIP removed");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <button onClick={toggle} aria-label={c.vip ? "Remove VIP" : "Mark as VIP"} data-testid={`customer-vip-toggle-${c.id}`}
      className={`transition-colors ${c.vip ? "text-amber-500" : "text-muted-foreground/50 hover:text-amber-500"}`}>
      <Star className="h-4 w-4" fill={c.vip ? "currentColor" : "none"} />
    </button>
  );
}

function NoteEditor({ c, onSaved, onClose }) {
  const [text, setText] = useState(c.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/owner/users/${c.id}/note`, { note: text });
      onSaved({ ...c, ...r.data }); toast.success("Note saved"); onClose();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-border bg-muted/40"><td colSpan={4} className="p-4">
      <Textarea data-testid={`customer-note-input-${c.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
        placeholder="Private note — only you can see this (e.g. referred by Mike, wants yearly plan in March)" className="min-h-[90px] bg-background" />
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" data-testid={`customer-note-save-${c.id}`} disabled={busy} onClick={save} className="bg-indigo-600 hover:bg-indigo-500 text-white">Save note</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        <span className="text-xs text-muted-foreground ml-auto">{text.length}/2000</span>
      </div>
    </td></tr>
  );
}

function CustomerRow({ c, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const patch = async (body, msg) => {
    setBusy(true);
    try { const r = await api.patch(`/owner/users/${c.id}`, body); onSaved({ ...c, ...r.data }); toast.success(msg); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const isOwner = c.role === "owner";
  return (
    <>
      <tr className={`border-b border-border last:border-0 align-middle ${editing ? "border-b-0" : ""}`} data-testid={`customer-row-${c.id}`}>
        <td className="py-3 pr-3">
          <div className="flex items-center gap-2">
            {!isOwner && <VipStar c={c} onSaved={onSaved} />}
            <span className="font-medium">{c.display_name || c.company_name || "—"}</span>
            {c.vip && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5" data-testid={`customer-vip-badge-${c.id}`}>VIP</span>}
            {c.suspended && <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 border border-red-500/40 rounded px-1.5 py-0.5" data-testid={`customer-suspended-badge-${c.id}`}>Suspended</span>}
          </div>
          <div className="text-xs text-muted-foreground break-all">{c.email}</div>
          {!isOwner && (
            <button onClick={() => setEditing(!editing)} data-testid={`customer-note-btn-${c.id}`}
              className="mt-1 text-xs text-left text-muted-foreground hover:text-foreground transition-colors inline-flex items-start gap-1 max-w-md">
              <StickyNote className="h-3 w-3 mt-0.5 flex-shrink-0" />
              {c.note ? <span className="line-clamp-2" data-testid={`customer-note-text-${c.id}`}>{c.note}</span> : <span>Add private note</span>}
            </button>
          )}
          {c.note && c.note_updated_at && <div className="text-[10px] text-muted-foreground/70 ml-4">edited {fmtDateTime(c.note_updated_at)}</div>}
        </td>
        <td className="py-3 pr-3 hidden md:table-cell text-xs text-muted-foreground">{fmtDate(c.created_at)}<br />{c.projects} projects</td>
        <td className="py-3 pr-3">
          {isOwner ? <span className="text-xs font-semibold">Owner</span> : (
            <Select value={c.plan || "free"} disabled={busy} onValueChange={(v) => {
              if (c.plan_source === "stripe" && v === "free" && !window.confirm("This customer pays through Stripe. Switching to Free also cancels their subscription at the end of the billing period. Continue?")) return;
              patch({ plan: v }, `${c.email} is now on ${PLAN_LABEL[v]}`);
            }}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`customer-plan-select-${c.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PLAN_LABEL).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`customer-plan-option-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {c.plan_source && !isOwner && <div className="text-[11px] text-muted-foreground mt-1">{SOURCE[c.plan_source]}</div>}
        </td>
        <td className="py-3 text-right">
          {!isOwner && (c.suspended ? (
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} data-testid={`customer-restore-${c.id}`}
              onClick={() => patch({ suspended: false }, "Access restored")}><RotateCcw className="h-3 w-3 mr-1" />Restore</Button>
          ) : (
            <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" disabled={busy} data-testid={`customer-suspend-${c.id}`}
              onClick={() => window.confirm(`Suspend ${c.email}? They'll be signed out and can't log in until restored.`) && patch({ suspended: true }, "Account suspended")}>
              <Ban className="h-3 w-3 mr-1" />Suspend</Button>
          ))}
        </td>
      </tr>
      {editing && <NoteEditor c={c} onSaved={onSaved} onClose={() => setEditing(false)} />}
    </>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [vipOnly, setVipOnly] = useState(false);
  const [rows, setRows] = useState(null);
  const load = useCallback(async (query, vip) => {
    const r = await api.get("/owner/users", { params: { q: query, vip_only: vip } });
    setRows(r.data);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q, vipOnly), 250);
    return () => clearTimeout(t);
  }, [q, vipOnly, load]);
  const saved = (u) => setRows((rs) => rs.map((x) => (x.id === u.id ? { ...x, ...u } : x)));
  const counts = (rows || []).reduce((a, r) => ({ ...a, [r.plan || "free"]: (a[r.plan || "free"] || 0) + 1, vip: a.vip + (r.vip ? 1 : 0) }), { vip: 0 });
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="customers-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-3xl font-bold flex items-center gap-2"><Users className="h-7 w-7" />Customers</h2>
          <p className="text-sm text-muted-foreground mt-1">Change plans, suspend access, star your VIPs and keep private notes. Only you can see this page.</p>
        </div>
        <div className="text-xs text-muted-foreground">Free {counts.free || 0} · Premium {counts.premium || 0} · Super Premium {counts.super_premium || 0} · VIP {counts.vip}</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customers-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, name or company" className="pl-9 h-11" />
        </div>
        <Button variant={vipOnly ? "default" : "outline"} data-testid="customers-vip-filter-btn" onClick={() => setVipOnly(!vipOnly)}
          className={`h-11 ${vipOnly ? "bg-amber-500 hover:bg-amber-400 text-slate-900" : ""}`}>
          <Star className="h-4 w-4 mr-2" fill={vipOnly ? "currentColor" : "none"} />VIP only
        </Button>
      </div>
      <div className="border border-border rounded-xl bg-card px-5 overflow-x-auto">
        {!rows ? <div className="py-8 text-sm text-muted-foreground">Loading…</div> : rows.length === 0 ? (
          <div className="py-8 text-sm text-muted-foreground" data-testid="customers-empty">{vipOnly ? "No VIP customers yet — tap the star next to a name." : "No customers match that search."}</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-3 font-semibold">Customer</th><th className="py-3 font-semibold hidden md:table-cell">Joined</th>
              <th className="py-3 font-semibold">Plan</th><th className="py-3 font-semibold text-right">Access</th>
            </tr></thead>
            <tbody>{rows.map((c) => <CustomerRow key={c.id} c={c} onSaved={saved} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
